import { Transition } from '@headlessui/react';
import xIcon from '@iconify/icons-heroicons-outline/x';
import React from 'react';
import { twMerge } from 'tailwind-merge';

import MemoizedIcon from '@components/MemoizedIcon';
import useBodyOverflow from '@hooks/useBodyOverflow';

export interface BottomSheetProps {
  children: React.ReactNode;
  isOpen: boolean;
  toggleOpen?: (isOpen: boolean) => void;
  title?: string | React.ReactNode;
  withoutHeader?: boolean;
  contentClassName?: string;
  dataTestId?: string;
}

const BottomSheet: React.FC<BottomSheetProps> = ({
  children,
  isOpen,
  toggleOpen,
  title,
  withoutHeader,
  contentClassName,
  dataTestId = 'bottom-sheet',
}) => {
  useBodyOverflow(isOpen);

  return (
    <div
      data-testid={dataTestId}
      className={twMerge(
        `fixed top-0 left-0 w-full h-full z-50`,
        isOpen ? 'pointer-events-auto' : 'pointer-events-none',
      )}
    >
      <Transition
        show={isOpen}
        enter="transition-transform duration-200"
        enterFrom="translate-y-full"
        enterTo="translate-y-0"
        leave="transition-transform duration-200"
        leaveFrom="translate-y-0"
        leaveTo="translate-y-full"
        className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl z-50"
      >
        {withoutHeader ? (
          <div className="flex w-full min-h-[20px] rounded-t-2xl" />
        ) : (
          <div className="flex w-full border-b rounded-t-2xl">
            <div className="px-5 py-4 flex-grow font-semibold">{title}</div>
            {toggleOpen && (
              <button
                className="flex flex-none items-center justify-center p-4 pl-0"
                data-testid="close-btn-bottom-sheet"
                onClick={() => toggleOpen(!isOpen)}
                type="button"
              >
                <MemoizedIcon icon={xIcon} className="text-[20px]" />
              </button>
            )}
          </div>
        )}
        <div
          className={twMerge(
            'flex flex-col overflow-y-auto max-h-[80vh]',
            contentClassName,
          )}
        >
          {children}
        </div>
      </Transition>
      <button
        data-testid="overlay-bottom-sheet"
        className={twMerge(
          'fixed top-0 left-0 w-full h-full transition',
          isOpen ? 'opacity-100 bg-black/50' : 'bg-transparent opacity-0',
        )}
        onClick={() => toggleOpen?.(false)}
      />
    </div>
  );
};

export default BottomSheet;
